/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';

// Local replica of @atlassian/switcher's `OrgUnitAvatar`, which is an internal, non-exported
// primitive (packages/navigation/atlassian-switcher/src/ui/primitives/org-unit-avatar.tsx). We
// duplicate it here rather than take a dependency on the switcher package. It renders a 28px rounded
// square with the first two letters of the site name and a deterministic accent colour. Light mode
// uses the `subtlest` accent background, dark mode the `subtler` one (matching OrgUnitAvatar).

const variants = ['purple', 'blue', 'lime', 'yellow'] as const;
type Variant = (typeof variants)[number];

const lightBgTokens: Record<Variant, string> = {
	purple: token('color.background.accent.purple.subtlest'),
	blue: token('color.background.accent.blue.subtlest'),
	lime: token('color.background.accent.lime.subtlest'),
	yellow: token('color.background.accent.yellow.subtlest'),
};

const darkBgTokens: Record<Variant, string> = {
	purple: token('color.background.accent.purple.subtler'),
	blue: token('color.background.accent.blue.subtler'),
	lime: token('color.background.accent.lime.subtler'),
	yellow: token('color.background.accent.yellow.subtler'),
};

const lightHoverBgTokens: Record<Variant, string> = {
	purple: token('color.background.accent.purple.subtlest.hovered'),
	blue: token('color.background.accent.blue.subtlest.hovered'),
	lime: token('color.background.accent.lime.subtlest.hovered'),
	yellow: token('color.background.accent.yellow.subtlest.hovered'),
};

const darkHoverBgTokens: Record<Variant, string> = {
	purple: token('color.background.accent.purple.subtler.hovered'),
	blue: token('color.background.accent.blue.subtler.hovered'),
	lime: token('color.background.accent.lime.subtler.hovered'),
	yellow: token('color.background.accent.yellow.subtler.hovered'),
};

const foregroundTokens: Record<Variant, string> = {
	purple: token('color.text.accent.purple.bolder'),
	blue: token('color.text.accent.blue.bolder'),
	lime: token('color.text.accent.lime.bolder'),
	yellow: token('color.text.accent.yellow.bolder'),
};

const styles = cssMap({
	initials: {
		font: token('font.body'),
		// eslint-disable-next-line @compiled/shorthand-property-sorting
		fontWeight: token('font.weight.medium'),
	},
	avatar: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		boxSizing: 'content-box',
		textTransform: 'capitalize',
		borderRadius: token('radius.large'),
		borderColor: 'transparent',
		borderStyle: 'solid',
		borderWidth: token('border.width.selected'),
		width: '28px',
		height: '28px',
		flex: 'none',
	},
});

// Deterministically pick a variant from the site name so a given site always gets the same colour.
const pickVariant = (seed: string): Variant => {
	const charCodeSum = seed.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
	return variants[charCodeSum % variants.length];
};

export interface SiteAvatarProps {
	/** Site name — its first two letters become the initials and it seeds the accent colour. */
	displayName: string;
	/** Renders with the hovered background token (e.g. for a selected/active row). */
	isActive?: boolean;
}

export const SiteAvatar = ({ displayName, isActive = false }: SiteAvatarProps): JSX.Element => {
	const { colorMode } = useThemeObserver();
	const isDarkMode = colorMode === 'dark';
	const variant = pickVariant(displayName);

	const backgroundColor = isActive
		? isDarkMode
			? darkHoverBgTokens[variant]
			: lightHoverBgTokens[variant]
		: isDarkMode
			? darkBgTokens[variant]
			: lightBgTokens[variant];
	const color = foregroundTokens[variant];

	return (
		// The colours are selected at runtime (accent variant from the site name × light/dark theme ×
		// active), so they're set via `style` rather than a static css map.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- dynamic, runtime-selected token values
		<div css={styles.avatar} style={{ backgroundColor, color }}>
			<span aria-hidden="true" css={styles.initials}>
				{displayName.substring(0, 2)}
			</span>
		</div>
	);
};
